import axios, { AxiosError, type AxiosResponse, type AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import generateId from '@stitch-fix/log-weasel';
import { recordMark } from '@stitch-fix/client-analytics-reporter';
import { browserLogger } from '../../loggers/browser';
import { fetchAwsWafToken } from './awsWafIntegration';
const appName = 'growth-next-ui';

/**
 * AxiosResponse and AxiosError contain a `config` object that may contain sensitive information, such as email and password
 * This function will remove the sensitive information from the payload so that it can be logged
 */
const formatConfig = (config: AxiosRequestConfig | undefined) => {
  if (!config) {
    return {};
  }
  return {
    config: {
      baseUrl: config.baseURL,
      headers: config.headers,
      url: config.url,
      ...(config.data && {
        data: 'REDACTED'
      })
    }
  };
};
const formatResponse = (response: AxiosResponse | undefined) => {
  if (!response) {
    return {};
  }
  // Redact the response data for all successful responses except for 202
  const isOkayReponse = response.status >= 200 && response.status < 300;
  const shouldRedactData = isOkayReponse && response.status !== 202;
  return {
    response: {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      data: shouldRedactData ? 'REDACTED' : response.data
    }
  };
};
const formatError = (error: AxiosError) => ({
  code: error.code,
  message: error.message,
  name: error.name,
  ...formatResponse(error.response),
  ...formatConfig(error.config)
});

// Custom error code to trigger a retry when AWS WAF responds with a challenge
const WAF_AXIOS_ERROR_CODE = 'AWS_WAF_ERROR';
const MAX_RETRIES = 1;
const CLIENT_AUTH_RESPONSE_MARK_NAME = 'axios_client_auth_response';

/**
 * AWS WAF responds with a 202 status code and a `x-amzn-waf-action` header set to `challenge`
 * when it wants the client to rechallenge, due to a missing, invalid, or expired token.
 * Docs: https://docs.aws.amazon.com/waf/latest/developerguide/waf-captcha-and-challenge-actions.html
 * Example: https://github.com/aws-samples/aws-waf-captcha-react-demo/blob/main/frontend/src/aws-waf-captcha/useAWSWAFCaptchaAxios.js#L34-L35
 */
const isChallengeRequired = (response: AxiosResponse) => response.status === 202 && response.headers['x-amzn-waf-action'] === 'challenge';

/**
 * Success interceptor is always called whenever a request responds with a 2xx status.
 * If the response requires a challenge, we need to throw an error with the WAF_AXIOS_ERROR_CODE
 * for axios-retry's `retryCondition` to trigger a retry.
 * axios-retry docs: https://github.com/softonic/axios-retry
 */
const responseSuccessInterceptor = (response: AxiosResponse) => {
  if (isChallengeRequired(response)) {
    throw new AxiosError('AwsWafIntegration: challenge token required', WAF_AXIOS_ERROR_CODE, response.config, response.request, response);
  }
  recordMark(CLIENT_AUTH_RESPONSE_MARK_NAME, {
    type: 'success',
    status: response.status,
    url: response.config.url
  });
  return response;
};

/**
 * Error interceptor is only used to log errors and send metrics.
 */
const responseErrorInterceptor = (error: AxiosError) => {
  recordMark(CLIENT_AUTH_RESPONSE_MARK_NAME, {
    type: 'error',
    // if there is no response status then it's most likely a network error code
    status: error.response?.status || error.code,
    url: error.response?.config.url
  });
  browserLogger({
    level: 'warn',
    team: 'client-onboarding',
    message: 'AwsWafIntegration: error response',
    context: {
      feature: 'aws-waf-integration',
      error: formatError(error)
    }
  });
  return Promise.reject(error);
};

/**
 * Creates an Axios instance with the necessary headers to interact with the AWS WAF Client Auth API.
 * We need to support two versions of the API; version 2 is used only for Google registration.
 */
export const getAWSWAFAxios = (apiVersion: '1' | '2') => {
  const wafClientAuthAxios = axios.create({
    baseURL: 'https://www.stitchfix.com/client-auth-api/api/',
    withCredentials: true,
    headers: {
      Accept: `application/json; version=${apiVersion}`,
      'Content-Type': 'application/json',
      /**
       * allows us to bypass CSRF
       * https://github.com/stitchfix/client-auth-api/pull/387/files#diff-a402442719412a456e9523a1be22ef9413a5161f29c176dfe2701e2413d3ba0cR7
       */
      'X-SF-NEXT-APP': appName,
      'X-Request-Id': generateId(appName)
    }
  });

  // Request interceptor to fetch the token before every request _and_ retry
  wafClientAuthAxios.interceptors.request.use(async config => {
    await fetchAwsWafToken();
    return config;
  });

  // Response interceptors **must** be added before calling axiosRetry
  // Related issue: https://github.com/softonic/axios-retry/issues/100#issuecomment-1015679894
  wafClientAuthAxios.interceptors.response.use(responseSuccessInterceptor, responseErrorInterceptor);
  axiosRetry(wafClientAuthAxios, {
    retries: MAX_RETRIES,
    retryCondition: error => error.code === WAF_AXIOS_ERROR_CODE,
    onRetry: async (retryCount, error) => {
      recordMark(CLIENT_AUTH_RESPONSE_MARK_NAME, {
        type: 'retry',
        status: error.response?.status || error.code,
        url: error.response?.config.url
      });
      browserLogger({
        level: 'info',
        team: 'client-onboarding',
        message: 'AwsWafIntegration: retrying request',
        context: {
          feature: 'aws-waf-integration',
          error: formatError(error),
          retryCount
        }
      });
    },
    onMaxRetryTimesExceeded: (error, retryCount) => {
      recordMark(CLIENT_AUTH_RESPONSE_MARK_NAME, {
        type: 'max_retries_exceeded',
        status: error.response?.status || error.code,
        url: error.response?.config.url
      });
      browserLogger({
        level: 'error',
        team: 'client-onboarding',
        message: 'AwsWafIntegration: max retries exceeded',
        context: {
          feature: 'aws-waf-integration',
          error: formatError(error),
          retryCount
        }
      });
    }
  });
  return wafClientAuthAxios;
};
export const wafClientAuthAxios = getAWSWAFAxios('1');
export const wafClientAuthV2Axios = getAWSWAFAxios('2');